import { queryKeys } from "@bso/shared";
import { createRoute, useParams } from "@tanstack/solid-router";
import { useNavigate } from "@/lib/u-router";
import { appRoute } from "./route";
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import {
  ALargeSmall,
  Bookmark,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Superscript,
} from "lucide-solid";
import {
  abbrToId,
  bookChapters,
  getBookNameDescriptor,
  getNextChapter,
  getPrevChapter,
  idToAbbr,
  isValidReference,
} from "@/utils/bible-books-data";
import { NotFoundState } from "@/components/ui/not-found-state";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { takeInitialSearchParam } from "@/lib/initial-search";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { type ReaderFontSize, readerStore } from "@/store/reader-store";
import { parseVerseSelection, serializeVerseSelection } from "@/utils/verse-selection";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { createPowerSyncQuery } from "@bso/shared/solid";
import {
  and,
  bible_bookmark,
  bible_verse_highlight,
  bible_version,
  eq,
  isNull,
} from "@bso/powersync";
import { formatVerseText } from "@/utils/verse-text";
import { VerseActionsBar } from "@/components/bible/verse-actions-bar";
import {
  loadChapter,
  ReaderNavSelector,
  type ReaderNavStep,
  type ReaderNavTarget,
  seedReaderContent,
  StrongConcordancePanel,
} from "@/features/reader";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { mergePointerHandlers, pointerHandlers } from "@/lib/gestures/solid";
import { vibrate } from "@/lib/gestures/haptics";
import { READER_SWIPE } from "@/lib/gestures/presets";
import { useReaderPullNav } from "@/features/reader/hooks/use-reader-pull-nav";
import { AppErrorBoundary } from "@/components/app-error-boundary";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

type ReaderCompose = "note" | "highlight";

type BibleSearch = {
  verse?: number;
  /**
   * Código Strong abierto en el panel de concordancia. Hace **compartible** la
   * lectura con su palabra Strong abierta (p.ej. `/bible/kjv/gen/1?strong=H7225`):
   * recargar/pegar la URL reabre el panel exactamente en ese código. Vacío =
   * panel cerrado, sin ensuciar la URL.
   */
  strong?: string;
  /**
   * Selección activa de versículos, forma compacta `4-6,9`. Hace la **selección
   * compartible**: pegar la URL reselecciona el rango y reabre la barra de
   * acciones. Ausente = sin selección.
   */
  sel?: string;
  /**
   * Tamaño de fuente del lector (`small|medium|large`). Modo de vista que viaja
   * en la URL para reproducir la lectura tal cual. Ausente = preferencia local
   * (localStorage).
   */
  fs?: ReaderFontSize;
  /**
   * Visibilidad de números Strong. `0` = ocultos, `1` = visibles. Ausente =
   * preferencia local. Forma canónica NÚMERO para que TanStack serialice el
   * deep-link limpio (`?strongs=0`, sin comillas JSON) y al re-leerlo el
   * `JSON.parse` del router lo devuelva como número que la coerción acepta.
   */
  strongs?: 0 | 1;
  /**
   * Editor abierto sobre la selección: `note` (nota) o `highlight` (etiquetas de
   * resaltado). Reabre el formulario al pegar/recargar la URL. Ausente = cerrado.
   */
  compose?: ReaderCompose;
  /**
   * Selector de navegación (libro → capítulo → versículo) abierto y su **paso
   * concreto**, para que CADA paso del drill-down tenga su propia URL copiable que
   * reabra exactamente esa pantalla en otro navegador:
   *   - `book`            → popup abierto en el paso de Libro.
   *   - `book:gen`        → paso de Capítulo del libro elegido (Génesis).
   *   - `book:gen:4`      → paso de Versículo del capítulo elegido (Génesis 4).
   * Ausente = popup cerrado. El destino final (elegir capítulo/versículo) navega a
   * la URL del lector y limpia este param.
   */
  nav?: string;
};

const STRONG_CODE_PATTERN = /^[HG]\d+$/;

/** Normaliza/valida un código Strong de la URL (omite valores basura). */
function parseStrongParam(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const code = value.trim().toUpperCase();
  return STRONG_CODE_PATTERN.test(code) ? code : undefined;
}

/** Saneo de `?fs=` a un tamaño de fuente válido (omite basura). */
function parseFontSizeParam(value: unknown): ReaderFontSize | undefined {
  return value === "small" || value === "medium" || value === "large" ? value : undefined;
}

/** Saneo de `?compose=` al editor válido (omite basura). */
function parseComposeParam(value: unknown): ReaderCompose | undefined {
  return value === "note" || value === "highlight" ? value : undefined;
}

/**
 * Saneo de `?nav=` a un paso de navegación válido. Formato `book[:abbr[:chapter]]`.
 * Cualquier libro/capítulo fuera de rango degrada al paso anterior válido (no
 * abre una vista rota), de modo que un id basura nunca produce un drill-down roto.
 */
function parseNavParam(value: unknown): ReaderNavStep | undefined {
  if (typeof value !== "string" || value.length === 0) {
    return undefined;
  }
  const [head, abbrRaw, chapterRaw] = value.split(":");
  if (head !== "book") {
    return undefined;
  }
  if (abbrRaw === undefined) {
    return { tab: "book" };
  }
  const abbr = abbrRaw.trim().toLowerCase();
  const bookId = abbrToId(abbr);
  // abbr inválido → degradamos al paso de libro (popup abierto, sin selección).
  // (`abbrToId` cae a Génesis ante un id desconocido; el round-trip lo detecta.)
  if (idToAbbr(bookId) !== abbr) {
    return { tab: "book" };
  }
  if (chapterRaw === undefined) {
    return { tab: "chapter", bookAbbr: abbr };
  }
  const chapter = Number.parseInt(chapterRaw, 10);
  const maxChapter = bookChapters[bookId] ?? 0;
  // Capítulo fuera de rango → degradamos al paso de capítulo del libro elegido.
  if (!Number.isFinite(chapter) || chapter < 1 || chapter > maxChapter) {
    return { tab: "chapter", bookAbbr: abbr };
  }
  return { tab: "verse", bookAbbr: abbr, chapter };
}

/** Serializa un paso de navegación a la forma canónica de `?nav=`. */
function serializeNavStep(step: ReaderNavStep): string {
  if (step.tab === "book" || !step.bookAbbr) {
    return "book";
  }
  if (step.tab === "chapter" || step.chapter === undefined) {
    return `book:${step.bookAbbr}`;
  }
  return `book:${step.bookAbbr}:${step.chapter}`;
}

export const bibleReaderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/bible/$version/$bookAbbr/$chapter",
  validateSearch: (search: Record<string, unknown>): BibleSearch => {
    const result: BibleSearch = {};
    const verseNum = search.verse ? Number(search.verse) : Number.NaN;
    if (Number.isFinite(verseNum) && verseNum > 0) {
      result.verse = verseNum;
    }
    const strong = parseStrongParam(search.strong);
    if (strong) {
      result.strong = strong;
    }
    // Re-serializamos a la forma canónica (`4-6,9`) para que la URL sea estable.
    const selVerses = parseVerseSelection(search.sel);
    if (selVerses.length > 0) {
      result.sel = serializeVerseSelection(selVerses);
    }
    const fs = parseFontSizeParam(search.fs);
    if (fs) {
      result.fs = fs;
    }
    // Forma canónica NÚMERO 0|1. Aceptamos en ENTRADA tanto número como string
    // ("0"/"1"/0/1): TanStack auto-parsea `?strongs=0` al NÚMERO 0 vía JSON.parse,
    // pero un enlace escrito a mano o una forma antigua puede llegar como string.
    // Normalizamos SIEMPRE a número → TanStack serializa `?strongs=0` limpio (sin
    // comillas JSON) y al re-leerlo el número round-trips y la coerción lo acepta.
    // (CC-18: el bug original era que la coerción RECHAZABA el número; ahora el
    // número ES la forma canónica, así que el param nunca cae a `undefined` ni el
    // router lo borra en el boot → el deep-link `?strongs=` se estabiliza y el
    // restore-poll ya no re-navega en bucle.)
    if (search.strongs === 0 || search.strongs === "0") {
      result.strongs = 0;
    } else if (search.strongs === 1 || search.strongs === "1") {
      result.strongs = 1;
    }
    const compose = parseComposeParam(search.compose);
    if (compose) {
      result.compose = compose;
    }
    // Re-serializamos `?nav=` a su forma canónica (`book`, `book:gen`,
    // `book:gen:4`) para que cada paso del selector tenga una URL estable.
    const navStep = parseNavParam(search.nav);
    if (navStep) {
      result.nav = serializeNavStep(navStep);
    }
    return result;
  },
  component: BibleReaderPage,
});

function BibleReaderPage() {
  const SCROLL_DELAY_MS = 200;
  const params = useParams({ from: "/u/$u/bible/$version/$bookAbbr/$chapter" });
  const navigate = useNavigate();

  const { t } = useI18n();
  const search = bibleReaderRoute.useSearch();
  const version = () => params().version;
  const bookAbbr = () => params().bookAbbr;
  const chapter = () => params().chapter;
  const bookId = () => abbrToId(bookAbbr());
  const bookName = () => t(getBookNameDescriptor(bookId()));

  // Una URL con libro o capítulo inválido (`/bible/kjv/zzz/3`, `/bible/kjv/gen/999`)
  // antes coercía a Génesis 1 / capítulo vacío, dejando el lector roto o cargando.
  // Detectamos la referencia inválida y mostramos "no encontrado" con salida segura.
  const validReference = createMemo(() => isValidReference(bookAbbr(), chapter()));

  // Database contexts
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  // Book → Chapter → Verse selector. Su paso ACTIVO vive en la URL (`?nav=`) para
  // que CADA paso del drill-down (popup abierto, capítulo-de-libro, versículo-de-
  // capítulo) tenga su propia URL copiable que reabra exactamente esa pantalla en
  // otro navegador. El componente del selector (feature `reader`) es controlado:
  // lee el paso desde `navStep()` y los avances de paso escriben a la URL.
  const navStep = (): ReaderNavStep | undefined => parseNavParam(search().nav);
  const isBookSelectorOpen = () => navStep() !== undefined;

  /** Abre/avanza el selector escribiendo el paso a la URL (sin inflar historial). */
  function setNavStep(step: ReaderNavStep | undefined) {
    void navigate({
      to: "/bible/$version/$bookAbbr/$chapter",
      params: { version: version(), bookAbbr: bookAbbr(), chapter: chapter() },
      search: (prev) => ({ ...prev, nav: step ? serializeNavStep(step) : undefined }),
      replace: true,
    });
  }

  function openBookSelector() {
    setNavStep({ tab: "book" });
  }

  function handleNavigate(target: ReaderNavTarget) {
    // Navegar al destino limpia `?nav=` (el selector se cierra) y deja la URL del
    // lector como única fuente; no cerramos el popup por separado para no pisar
    // esta navegación con un segundo `replace` a la base (bug canónico Patrón A).
    void navigate({
      to: "/bible/$version/$bookAbbr/$chapter",
      params: {
        version: version(),
        bookAbbr: target.abbr,
        chapter: String(target.chapter),
      },
      search: target.verse ? { verse: target.verse } : {},
    });
  }

  // Strong dictionary drawer state. Reflejado en la URL (`?strong=`) para que la
  // lectura con su concordancia abierta sea compartible/recargable; el signal
  // local es la fuente reactiva de render y un `createEffect` lo mantiene en sync
  // con la URL (back/forward + enlace pegado en otra pestaña).
  const [selectedStrongCode, setSelectedStrongCodeSignal] = createSignal<string | null>(
    search().strong ?? null,
  );

  function setSelectedStrongCode(code: string | null) {
    setSelectedStrongCodeSignal(code);
    void navigate({
      to: "/bible/$version/$bookAbbr/$chapter",
      params: { version: version(), bookAbbr: bookAbbr(), chapter: chapter() },
      search: (prev) => ({ ...prev, strong: code ?? undefined }),
      replace: true,
    });
  }

  // Version selector dropdown state
  const [showVersionDropdown, setShowVersionDropdown] = createSignal(false);

  // Available bible versions replicated locally via PowerSync
  const versionsQuery = createPowerSyncQuery({
    queryKey: () => queryKeys.bible.versionsLite(),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select({ id: bible_version.id, abbr: bible_version.abbr, name: bible_version.name })
        .from(bible_version);
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()),
  });

  // Cross-book chapter navigation (legacy parity: prev-next-chapter-helper)
  const prevChapterRef = () => getPrevChapter(bookId(), Number.parseInt(chapter(), 10));
  const nextChapterRef = () => getNextChapter(bookId(), Number.parseInt(chapter(), 10));

  function navigateToChapter(ref: { bookId: number; chapter: number } | undefined) {
    if (!ref) {
      return;
    }
    void navigate({
      to: "/bible/$version/$bookAbbr/$chapter",
      params: {
        version: version(),
        bookAbbr: idToAbbr(ref.bookId),
        chapter: String(ref.chapter),
      },
    });
  }

  function handleVersionChange(abbr: string) {
    setShowVersionDropdown(false);
    void navigate({
      to: "/bible/$version/$bookAbbr/$chapter",
      params: {
        version: abbr.toLowerCase(),
        bookAbbr: bookAbbr(),
        chapter: chapter(),
      },
    });
  }

  // Keyboard navigation between chapters (skip while typing or with the
  // book/chapter dialog open)
  function handleGlobalKeyDown(e: KeyboardEvent) {
    if (isBookSelectorOpen()) {
      return;
    }
    const { target } = e;
    if (
      target instanceof HTMLElement &&
      (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
    ) {
      return;
    }
    if (e.key === "ArrowLeft") {
      navigateToChapter(prevChapterRef());
    } else if (e.key === "ArrowRight") {
      navigateToChapter(nextChapterRef());
    }
  }

  // --- Swipe between chapters (carousel) ----------------------------------
  // The reader panel tracks the finger horizontally; on commit (or fling) the
  // current chapter slides out and navigation loads the next/previous one.
  // Tuning lives in READER_SWIPE; the physics (axis-lock, deadzone, fling) come
  // from the shared recognizer, so this matches the gesture standard app-wide.
  // `touch-action: pan-y` keeps native vertical scroll while we claim the
  // horizontal axis. Keyboard nav (← →) is untouched, and reduced-motion users
  // get an instant jump with no slide.
  const [dragX, setDragX] = createSignal(0);
  const [swipeTransitioning, setSwipeTransitioning] = createSignal(false);
  const [swipeRef, setSwipeRef] = createSignal<HTMLDivElement>();

  const panelWidth = () => swipeRef()?.clientWidth ?? READER_SWIPE.fallbackWidthPx;

  /** Animate a directional chapter change. delta>0 = next (slides left). */
  function animateChapter(delta: number) {
    const ref = delta > 0 ? nextChapterRef() : prevChapterRef();
    if (!ref) {
      // No neighbour in that direction: snap back instead of leaving a gap.
      setSwipeTransitioning(true);
      setDragX(0);
      return;
    }
    if (prefersReducedMotion()) {
      setDragX(0);
      navigateToChapter(ref);
      return;
    }
    const width = panelWidth();
    const outX = delta > 0 ? -width : width; // current chapter leaves toward travel
    setSwipeTransitioning(true);
    setDragX(outX);
    vibrate(READER_SWIPE.hapticMs);
    window.setTimeout(() => {
      // Navigation remounts the route content, so reset the transform first.
      setSwipeTransitioning(false);
      setDragX(0);
      navigateToChapter(ref);
    }, READER_SWIPE.transitionMs);
  }

  function snapBack() {
    setSwipeTransitioning(true);
    setDragX(0);
  }

  const swipeRecognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: READER_SWIPE.thresholdPx,
    fling: { ...READER_SWIPE.fling },
    onStart: () => {
      setSwipeTransitioning(false);
    },
    onMove: (sample) => {
      setSwipeTransitioning(false);
      setDragX(sample.dx);
    },
    onEnd: (sample) => {
      const commitPx = Math.min(
        READER_SWIPE.commitThresholdPx,
        panelWidth() * READER_SWIPE.commitThresholdRatio,
      );
      const committed = sample.isFling || Math.abs(sample.dx) >= commitPx;
      if (committed) {
        // Swipe left (dx<0) → next chapter; swipe right (dx>0) → previous.
        animateChapter(sample.dx < 0 ? 1 : -1);
      } else {
        snapBack();
      }
    },
    onCancel: snapBack,
  });

  const swipeHandlers = pointerHandlers(swipeRecognizer);

  // --- Pull-down to open the book/chapter/verse selector ------------------
  // From the top of the reader, dragging downward past the commit threshold
  // opens the nav selector (the inverse of the Dialog's pull-to-dismiss). Shares
  // the same pointer stream as the horizontal chapter swipe; axis-lock means
  // only the gesture matching the drag direction engages. `canEngage` gates it
  // to scrollTop 0 so reading scroll is never hijacked.
  const pullNav = useReaderPullNav({
    getScrollTop: () => swipeRef()?.scrollTop ?? 0,
    prefersReducedMotion,
    onOpen: () => {
      openBookSelector();
    },
  });

  const readerHandlers = mergePointerHandlers(swipeHandlers, pullNav.handlers);

  // Combine horizontal swipe and vertical pull into one transform. Only one axis
  // is ever non-zero at a time (the recognizers axis-lock), so they never fight.
  const swipeTransform = () => `translate3d(${dragX()}px, ${pullNav.pullY()}px, 0)`;
  const swipeTransition = () =>
    swipeTransitioning() || pullNav.settling()
      ? `transform ${READER_SWIPE.transitionMs}ms ease-out`
      : "none";

  // Reference for scroll container to listen to clicks imperatively
  let containerRef: HTMLDivElement | undefined = undefined;

  onMount(() => {
    containerRef?.addEventListener("click", handleChapterClick);
    globalThis.addEventListener("keydown", handleGlobalKeyDown);
  });

  onCleanup(() => {
    containerRef?.removeEventListener("click", handleChapterClick);
    globalThis.removeEventListener("keydown", handleGlobalKeyDown);
    swipeRecognizer.cancel();
    pullNav.cancel();
  });

  createEffect(() => {
    const v = version();
    const b = bookAbbr();
    const c = chapter();
    if (v && b && c) {
      localStorage.setItem(
        "last-read-bible",
        JSON.stringify({ version: v, bookAbbr: b, chapter: c }),
      );
    }
  });

  // Restaura el deep-link de arranque (`?strong=`/`?verse=`) si el boot del router
  // lo perdió: el primer commit diferido de TanStack (`flush` en microtask) puede
  // reescribir la URL SIN los search params, y lo hace DESPUÉS del `onMount` de
  // esta ruta. Por eso no basta restaurar una vez al montar: tomamos el snapshot
  // inicial (capturado de forma síncrona antes del flush) y, mediante un efecto,
  // lo re-aplicamos mientras la URL aún no lo refleje. Tras confirmarlo estable
  // unos ticks (el flush ya no volverá a borrarlo), desarmamos y dejamos de tocar
  // la URL → idempotente, y las navegaciones internas posteriores mandan.
  const bootStrong = parseStrongParam(takeInitialSearchParam("strong", "/bible/") ?? undefined);
  const bootVerseRaw = takeInitialSearchParam("verse", "/bible/");
  const bootVerseNum = bootVerseRaw ? Number(bootVerseRaw) : Number.NaN;
  const bootVerse = Number.isFinite(bootVerseNum) && bootVerseNum > 0 ? bootVerseNum : undefined;
  // Estado de vista compartible que el flush del router también puede perder al
  // arrancar por deep-link (`?sel=`, `?fs=`, `?strongs=`, `?compose=`).
  const bootSelVerses = parseVerseSelection(takeInitialSearchParam("sel", "/bible/") ?? undefined);
  const bootSel = bootSelVerses.length > 0 ? serializeVerseSelection(bootSelVerses) : undefined;
  const bootFs = parseFontSizeParam(takeInitialSearchParam("fs", "/bible/") ?? undefined);
  // El snapshot de arranque se lee del texto CRUDO de la URL, así que llega como
  // string ("0"/"1"); lo normalizamos a la forma canónica NÚMERO 0|1 para que
  // case exactamente con `search().strongs` en el restore-poll (sin ese casteo,
  // `"0" === 0` sería false y el poll re-navegaría en bucle — el síntoma CC-18).
  const bootStrongsRaw = takeInitialSearchParam("strongs", "/bible/");
  const bootStrongs = bootStrongsRaw === "0" ? 0 : bootStrongsRaw === "1" ? 1 : undefined;
  const bootCompose = parseComposeParam(takeInitialSearchParam("compose", "/bible/") ?? undefined);
  const bootNavStep = parseNavParam(takeInitialSearchParam("nav", "/bible/") ?? undefined);
  const bootNav = bootNavStep ? serializeNavStep(bootNavStep) : undefined;
  if (
    bootStrong !== undefined ||
    bootVerse !== undefined ||
    bootSel !== undefined ||
    bootFs !== undefined ||
    bootStrongs !== undefined ||
    bootCompose !== undefined ||
    bootNav !== undefined
  ) {
    // Ventana de restauración corta tras el montaje: re-aplica el deep-link de
    // arranque mientras la URL no lo refleje, porque el borrado del flush ocurre en
    // un microtask DESPUÉS del primer efecto. Sondeamos cada 150 ms hasta que la
    // URL ya traiga el param (y se asiente) o venza la ventana; entonces el sondeo
    // termina y el snapshot deja de mandar (cerrar el panel, etc. queda libre).
    const RESTORE_POLL_MS = 150;
    const RESTORE_MAX_TICKS = 14; // ~2.1 s
    const [restoreTick, setRestoreTick] = createSignal(0);
    // Confirmaciones consecutivas de que la URL ya refleja el deep-link. Exigimos
    // varias seguidas para asegurarnos de que el flush del router (que borra los
    // params en un microtask) ya pasó y no volverá a clobberear. Solo entonces
    // desarmamos, de modo que cerrar el panel después no lo reabra.
    const REQUIRED_STABLE_TICKS = 3;
    let stableCount = 0;
    let disarmed = false;
    createEffect(() => {
      const tick = restoreTick();
      if (disarmed) {
        return;
      }
      const current = search();
      const strongMatches = bootStrong === undefined || current.strong === bootStrong;
      const verseMatches = bootVerse === undefined || current.verse === bootVerse;
      const selMatches = bootSel === undefined || current.sel === bootSel;
      const fsMatches = bootFs === undefined || current.fs === bootFs;
      const strongsMatches = bootStrongs === undefined || current.strongs === bootStrongs;
      const composeMatches = bootCompose === undefined || current.compose === bootCompose;
      const navMatches = bootNav === undefined || current.nav === bootNav;
      if (
        strongMatches &&
        verseMatches &&
        selMatches &&
        fsMatches &&
        strongsMatches &&
        composeMatches &&
        navMatches
      ) {
        stableCount += 1;
        if (stableCount >= REQUIRED_STABLE_TICKS) {
          disarmed = true;
          return;
        }
      } else {
        stableCount = 0;
        void navigate({
          to: "/bible/$version/$bookAbbr/$chapter",
          params: { version: version(), bookAbbr: bookAbbr(), chapter: chapter() },
          search: (prev) => ({
            ...prev,
            ...(bootStrong ? { strong: bootStrong } : {}),
            ...(bootVerse ? { verse: bootVerse } : {}),
            ...(bootSel ? { sel: bootSel } : {}),
            ...(bootFs ? { fs: bootFs } : {}),
            ...(bootStrongs !== undefined ? { strongs: bootStrongs } : {}),
            ...(bootCompose ? { compose: bootCompose } : {}),
            ...(bootNav ? { nav: bootNav } : {}),
          }),
          replace: true,
        });
      }
      if (tick >= RESTORE_MAX_TICKS) {
        disarmed = true;
        return;
      }
      const id = setTimeout(() => {
        setRestoreTick((n) => n + 1);
      }, RESTORE_POLL_MS);
      onCleanup(() => {
        clearTimeout(id);
      });
    });
  }

  // Refleja `?strong=` (URL) → signal local: abre el panel al cargar por URL,
  // navegación atrás/adelante o enlace pegado. Idempotente: el valor de la URL
  // manda; sin él, el panel queda cerrado.
  createEffect(() => {
    const code = search().strong ?? null;
    if (code !== selectedStrongCode()) {
      setSelectedStrongCodeSignal(code);
    }
  });

  // --- Estado de vista del lector reflejado en la URL ---------------------
  // Selección de versículos (`?sel=`), tamaño de fuente (`?fs=`) y visibilidad
  // de Strong (`?strongs=`) son ahora **compartibles**: el valor de la URL manda
  // (URL→store) y los toggles del header escriben a la URL (store→URL vía
  // `replace`). Así pegar/recargar la URL reproduce la pantalla en otro navegador.

  /** Escribe el estado de vista a la URL sin inflar el historial. */
  function patchReaderSearch(patch: Partial<BibleSearch>) {
    void navigate({
      to: "/bible/$version/$bookAbbr/$chapter",
      params: { version: version(), bookAbbr: bookAbbr(), chapter: chapter() },
      search: (prev) => ({ ...prev, ...patch }),
      replace: true,
    });
  }

  // URL `?sel=` → store. El valor de la URL es la fuente de verdad de la selección.
  createEffect(() => {
    const fromUrl = parseVerseSelection(search().sel);
    const inStore = readerStore.getSelectedVersesArray();
    // Compara por contenido para no re-fijar (y romper la reactividad) en bucle.
    const equal = fromUrl.length === inStore.length && fromUrl.every((v, i) => v === inStore[i]);
    if (!equal) {
      readerStore.setSelection(fromUrl);
    }
  });

  // URL `?fs=` → store (si difiere). Sin el param, manda la preferencia local.
  createEffect(() => {
    const { fs } = search();
    if (fs && fs !== readerStore.fontSize) {
      readerStore.setFontSize(fs);
    }
  });

  // URL `?strongs=` → store (si difiere). Sin el param, manda la preferencia local.
  createEffect(() => {
    const raw = search().strongs;
    if (raw !== undefined) {
      const show = raw === 1;
      if (show !== readerStore.showStrongs) {
        readerStore.setShowStrongs(show);
      }
    }
  });

  // El editor (`?compose=`) solo es válido si hay versículos seleccionados; al
  // limpiar la selección, descartamos el compose para no dejar la URL inconsistente.
  const composeMode = (): ReaderCompose | undefined =>
    readerStore.getSelectedVersesArray().length > 0 ? search().compose : undefined;

  // Verses of the chapter, OFFLINE-FIRST: base PowerSync local → Tauri nativo →
  // API HTTP. La fuente del MVP es la semilla local (bible_verse_content).
  const [verses, { refetch: refetchVerses }] = createResource(
    () => ({
      version: version(),
      book: bookId(),
      chapter: Number.parseInt(chapter(), 10),
      // Re-ejecuta al estar listo el cliente drizzle (la semilla necesita la BD).
      ready: Boolean(drizzle()),
    }),
    async (query) => loadChapter(drizzle(), query.version, query.book, query.chapter),
  );

  // Resuelve el CONTENIDO del capítulo (la referencia ya es válida): presente /
  // cargando / no-encontrado / offline. Una referencia válida cuyo texto aún no
  // bajó/sembró (p.ej. una versión no replicada estando offline) antes dejaba el
  // lector en blanco; aquí distinguimos "offline, aún sin sincronizar" de "no hay
  // contenido para esta versión" en lugar de mostrar un vacío engañoso.
  const contentResolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => (verses()?.length ?? 0) > 0,
    isPending: () => verses.loading,
  });

  // Siembra el texto bíblico de arranque (KJV) en la base local en cuanto el
  // cliente drizzle esté disponible, para que el lector funcione offline; tras
  // sembrar, revalida el capítulo para tomar el texto recién sembrado.
  createEffect(() => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    void (async () => {
      await seedReaderContent(dbInstance);
      await refetchVerses();
    })();
  });

  // Scroll to targeted verse if provided in search params. Solo desplaza; ya no
  // muta la selección directamente (eso lo gobierna `?sel=` ahora). Si el deep
  // link no traía `?sel=`, sembramos la selección del versículo objetivo en la
  // URL para que quede resaltado y sea compartible de forma coherente.
  createEffect(() => {
    const target = search().verse;
    if (target && !verses.loading) {
      if (search().sel === undefined) {
        patchReaderSearch({ sel: serializeVerseSelection([target]) });
      }
      setTimeout(() => {
        const el = document.querySelector(`#verse-${target}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, SCROLL_DELAY_MS);
    }
  });

  // Query highlights reactively from PowerSync local instance using exact numeric bookId
  const highlights = createPowerSyncQuery({
    queryKey: () => queryKeys.bible.chapterHighlights(version(), bookId(), chapter(), userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = userId();
      const bId = bookId();
      const chNum = Number.parseInt(chapter(), 10);
      if (!dbInstance || !u || Number.isNaN(bId) || Number.isNaN(chNum)) {
        return null;
      }
      return dbInstance
        .select({
          id: bible_verse_highlight.id,
          verse: bible_verse_highlight.verse,
          color: bible_verse_highlight.color,
        })
        .from(bible_verse_highlight)
        .where(
          and(
            eq(bible_verse_highlight.user_id, u),
            eq(bible_verse_highlight.version, version()),
            eq(bible_verse_highlight.book, bId),
            eq(bible_verse_highlight.chapter, chNum),
            isNull(bible_verse_highlight.deleted_at),
          ),
        );
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(userId()),
  });

  // Fast O(1) index lookup map for highlights
  const highlightMap = createMemo(() => {
    const map = new Map<number, string>();
    const list = highlights.data ?? [];
    for (const h of list) {
      if (h.verse !== null && h.color) {
        map.set(h.verse, h.color);
      }
    }
    return map;
  });

  // Bookmarks/favorites of this chapter (personal user data, synced via PowerSync)
  const bookmarks = createPowerSyncQuery({
    queryKey: () => queryKeys.bible.chapterBookmarks(version(), bookId(), chapter(), userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = userId();
      const bId = bookId();
      const chNum = Number.parseInt(chapter(), 10);
      if (!dbInstance || !u || Number.isNaN(bId) || Number.isNaN(chNum)) {
        return null;
      }
      return dbInstance
        .select({ verse: bible_bookmark.verse })
        .from(bible_bookmark)
        .where(
          and(
            eq(bible_bookmark.user_id, u),
            eq(bible_bookmark.version, version()),
            eq(bible_bookmark.book, bId),
            eq(bible_bookmark.chapter, chNum),
            isNull(bible_bookmark.deleted_at),
          ),
        );
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(userId()),
  });

  // Fast O(1) lookup set for the bookmark indicator and the toggle decision
  const bookmarkSet = createMemo(() => {
    const set = new Set<number>();
    for (const b of bookmarks.data ?? []) {
      if (b.verse !== null) {
        set.add(b.verse);
      }
    }
    return set;
  });

  // Event delegation handler for Strong's clicks
  function handleChapterClick(e: MouseEvent) {
    const { target } = e;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    const strongCode = target.dataset.strong;

    if (strongCode) {
      e.stopPropagation();
      setSelectedStrongCode(strongCode);
    }
  }

  // Handler to toggle verse selection state. Escribe a la URL (`?sel=`) — la
  // fuente de verdad — para que la selección sea compartible/recargable; el
  // efecto URL→store actualiza el render. Al vaciar la selección, también
  // descartamos `?compose=` (un editor sin versículos no tiene sentido).
  function handleVerseClick(verseNumber: number) {
    const current = new Set(readerStore.getSelectedVersesArray());
    if (current.has(verseNumber)) {
      current.delete(verseNumber);
    } else {
      current.add(verseNumber);
    }
    const next = serializeVerseSelection([...current]);
    patchReaderSearch({
      sel: next === "" ? undefined : next,
      ...(next === "" ? { compose: undefined } : {}),
    });
  }

  // Listen for Escape key to close dictionary drawer
  createEffect(() => {
    if (selectedStrongCode()) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          setSelectedStrongCode(null);
        }
      };
      globalThis.addEventListener("keydown", handleKeyDown);
      onCleanup(() => {
        globalThis.removeEventListener("keydown", handleKeyDown);
      });
    }
  });

  return (
    <Show
      when={validReference()}
      fallback={
        <div class="flex h-full w-full items-center justify-center bg-background text-foreground">
          <NotFoundState
            variant="not-found"
            title={t(msg`Passage not found`)}
            description={t(
              msg`That book or chapter doesn't exist. Check the reference and try again.`,
            )}
            primaryAction={{
              label: t(msg`Go to Genesis 1`),
              onClick: () => {
                void navigate({
                  to: "/bible/$version/$bookAbbr/$chapter",
                  params: { version: version(), bookAbbr: "gen", chapter: "1" },
                });
              },
            }}
          />
        </div>
      }
    >
      <div class="flex h-full w-full overflow-hidden bg-background text-foreground relative">
        {/* Book → Chapter → Verse navigation selector (reader feature) */}
        <ReaderNavSelector
          open={isBookSelectorOpen()}
          step={navStep()}
          onStepChange={setNavStep}
          onClose={() => {
            setNavStep(undefined);
          }}
          version={version()}
          currentBookAbbr={bookAbbr()}
          currentChapter={Number.parseInt(chapter(), 10)}
          db={drizzle()}
          onNavigate={handleNavigate}
        />

        {/* Main Bible Reader Panel */}
        <div class="flex flex-1 flex-col overflow-hidden relative">
          {/* Reader Top Bar */}
          <div class="flex items-center justify-between border-b border-border/40 bg-card/35 backdrop-blur-lg px-2 sm:px-4 md:px-6 py-2.5 md:py-3 shrink-0 gap-1.5 md:gap-2">
            <div class="flex items-center gap-1.5 md:gap-2 min-w-0">
              <button
                type="button"
                onClick={() => {
                  navigateToChapter(prevChapterRef());
                }}
                disabled={!prevChapterRef()}
                class="flex items-center justify-center min-h-11 min-w-11 p-2 rounded-xl bg-muted/40 hover:bg-muted/70 text-foreground border border-border/50 transition-all active:scale-95 disabled:opacity-30 disabled:pointer-events-none shrink-0"
                title={t(msg`Previous chapter (←)`)}
                aria-label={t(msg`Previous chapter`)}
              >
                <ChevronLeft class="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={openBookSelector}
                data-testid="reader-nav-trigger"
                class="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 min-h-11 rounded-xl bg-muted/40 hover:bg-muted/70 text-foreground font-bold transition-all border border-border/50 text-sm md:text-base active:scale-95 min-w-0"
              >
                <span class="truncate">
                  {bookName()} {chapter()}
                </span>
                <ChevronDown class="w-4 h-4 text-muted-foreground shrink-0" />
              </button>

              <button
                type="button"
                onClick={() => {
                  navigateToChapter(nextChapterRef());
                }}
                disabled={!nextChapterRef()}
                class="flex items-center justify-center min-h-11 min-w-11 p-2 rounded-xl bg-muted/40 hover:bg-muted/70 text-foreground border border-border/50 transition-all active:scale-95 disabled:opacity-30 disabled:pointer-events-none shrink-0"
                title={t(msg`Next chapter (→)`)}
                aria-label={t(msg`Next chapter`)}
              >
                <ChevronRight class="w-4 h-4" />
              </button>
            </div>

            <div class="flex items-center gap-1.5 md:gap-2 shrink-0">
              {/* Bible version selector */}
              <div class="relative">
                <button
                  type="button"
                  onClick={() => setShowVersionDropdown(!showVersionDropdown())}
                  class="flex items-center justify-center gap-1 md:gap-1.5 px-2.5 md:px-3 py-2 min-h-11 min-w-11 rounded-xl bg-muted/40 hover:bg-muted/70 text-foreground font-bold text-2xs md:text-xs border border-border/50 transition-all active:scale-95"
                  title={t(msg`Change Bible version`)}
                >
                  <span>{version().toUpperCase()}</span>
                  <ChevronDown class="w-3.5 h-3.5 text-muted-foreground hidden sm:block" />
                </button>

                <Show when={showVersionDropdown()}>
                  <div class="absolute right-0 mt-2 w-56 max-h-72 overflow-y-auto bg-card border border-border/60 rounded-xl shadow-lg z-50 p-2">
                    <span class="block px-2 pb-2 border-b border-border/20 mb-1 text-3xs text-muted-foreground font-semibold uppercase">
                      {t(msg`Bible Version`)}
                    </span>
                    <For
                      each={versionsQuery.data ?? []}
                      fallback={
                        <p class="px-3 py-2 text-xs text-muted-foreground">
                          {t(msg`No versions synchronized.`)}
                        </p>
                      }
                    >
                      {(v) => (
                        <button
                          type="button"
                          onClick={() => {
                            if (v.abbr) {
                              handleVersionChange(v.abbr);
                            }
                          }}
                          class="w-full text-left px-3 py-1.5 text-xs rounded-lg hover:bg-muted/40 font-medium transition-colors flex items-center justify-between gap-2"
                          classList={{
                            "bg-primary/10 text-primary font-semibold":
                              v.abbr?.toLowerCase() === version(),
                            "text-foreground/80": v.abbr?.toLowerCase() !== version(),
                          }}
                        >
                          <span class="font-bold">{v.abbr?.toUpperCase()}</span>
                          <span class="text-muted-foreground truncate">{v.name}</span>
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
              </div>

              {/* Font size cycle (legacy parity: FontSizeToggleButton).
                Escribe el resultado a la URL (`?fs=`) para que el modo de vista
                sea compartible; el efecto URL→store aplica el cambio. */}
              <button
                type="button"
                onClick={() => {
                  const nextSize: Record<ReaderFontSize, ReaderFontSize> = {
                    small: "medium",
                    medium: "large",
                    large: "small",
                  };
                  patchReaderSearch({ fs: nextSize[readerStore.fontSize] });
                }}
                class="flex items-center justify-center min-h-11 min-w-11 p-2 rounded-xl bg-muted/40 hover:bg-muted/70 text-foreground border border-border/50 transition-all active:scale-95"
                title={(() => {
                  const size = readerStore.fontSize;
                  return t(msg`Font size: ${size}`);
                })()}
                aria-label={t(msg`Change font size`)}
              >
                <ALargeSmall class="w-4 h-4" />
              </button>

              {/* Strong numbers visibility toggle (legacy parity: CodeStringIcon
                header button). Escribe a la URL la forma canónica NÚMERO
                (`?strongs=0` oculto / `1` visible) → deep-link limpio y
                compartible; el efecto URL→store aplica el cambio. */}
              <button
                type="button"
                onClick={() => {
                  patchReaderSearch({ strongs: readerStore.showStrongs ? 0 : 1 });
                }}
                class="flex items-center justify-center min-h-11 min-w-11 p-2 rounded-xl border transition-all active:scale-95"
                classList={{
                  "bg-primary/15 border-primary/40 text-primary": readerStore.showStrongs,
                  "bg-muted/40 hover:bg-muted/70 border-border/50 text-muted-foreground":
                    !readerStore.showStrongs,
                }}
                title={
                  readerStore.showStrongs
                    ? t(msg`Hide Strong numbers`)
                    : t(msg`Show Strong numbers`)
                }
                aria-label={t(msg`Toggle Strong numbers`)}
                aria-pressed={readerStore.showStrongs}
              >
                <Superscript class="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Reader Content / Scroll Container.
            Owns the horizontal swipe-between-chapters gesture: `touch-action:
            pan-y` keeps native vertical scroll while the recognizer claims the
            horizontal axis (preventDefaults engaged moves). */}
          <div
            ref={(el) => {
              containerRef = el;
              setSwipeRef(el);
            }}
            class="flex-1 overflow-y-auto px-3 py-5 sm:p-6 md:p-8 relative"
            style={{ "touch-action": "pan-y" }}
            {...readerHandlers}
          >
            {/* Pull-down hint: fades in as the page is dragged down from the top,
              telling the reader the release will open the nav selector. */}
            <Show when={pullNav.pullY() > 4}>
              <div
                class="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground select-none"
                style={{
                  opacity: String(Math.min(1, pullNav.pullY() / 72)),
                  transform: `translateY(${Math.min(pullNav.pullY() * 0.5, 28)}px)`,
                }}
              >
                <ChevronDown class="w-3 h-3" />
                <span>{t(msg`Pull to browse`)}</span>
              </div>
            </Show>
            <div
              class="max-w-3xl mx-auto w-full space-y-1 pb-24 will-change-transform"
              role="listbox"
              aria-multiselectable="true"
              aria-label={t(msg`Chapter verses`)}
              style={{ transform: swipeTransform(), transition: swipeTransition() }}
            >
              <Show
                when={contentResolution() !== "loading"}
                fallback={
                  <div class="space-y-4 animate-pulse">
                    <div class="h-4 bg-muted/65 rounded w-3/4" />
                    <div class="h-4 bg-muted/65 rounded w-5/6" />
                    <div class="h-4 bg-muted/65 rounded w-2/3" />
                    <div class="h-4 bg-muted/65 rounded w-4/5" />
                  </div>
                }
              >
                <Show
                  when={contentResolution() === "ready"}
                  fallback={
                    <NotFoundState
                      variant={contentResolution() === "offline" ? "offline" : "not-found"}
                      title={
                        contentResolution() === "offline"
                          ? undefined
                          : t(msg`Passage not available`)
                      }
                      description={
                        contentResolution() === "offline"
                          ? undefined
                          : t(
                              msg`This chapter hasn't been downloaded for this version yet. Try another version or download it in Settings.`,
                            )
                      }
                      primaryAction={{
                        label: t(msg`Open settings`),
                        onClick: () => {
                          void navigate({ to: "/settings" });
                        },
                      }}
                    />
                  }
                >
                  <For each={verses()}>
                    {(verse) => (
                      <div
                        id={`verse-${verse.verse}`}
                        data-highlighted={highlightMap().has(verse.verse) ? "true" : undefined}
                        onClick={() => {
                          handleVerseClick(verse.verse);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            handleVerseClick(verse.verse);
                          }
                        }}
                        role="option"
                        aria-selected={Boolean(readerStore.selectedVerses[verse.verse])}
                        tabindex="0"
                        class="group relative flex items-start gap-1 py-3 px-4 rounded-xl border border-transparent border-l-4 transition-all duration-200 cursor-pointer select-none active:scale-[0.99] outline-none"
                        classList={{
                          "ring-2 ring-primary/30 bg-primary/8 border-l-primary shadow-sm": Boolean(
                            readerStore.selectedVerses[verse.verse],
                          ),
                          "hover:bg-muted/15": !readerStore.selectedVerses[verse.verse],
                          "border-l-transparent":
                            !highlightMap().has(verse.verse) &&
                            !readerStore.selectedVerses[verse.verse],
                        }}
                        style={{
                          "background-color": highlightMap().has(verse.verse)
                            ? `${highlightMap().get(verse.verse)}1F`
                            : undefined,
                          "border-left-color": highlightMap().has(verse.verse)
                            ? highlightMap().get(verse.verse)
                            : undefined,
                        }}
                      >
                        <span class="align-super text-xs font-bold text-accent mr-2.5 select-none shrink-0 mt-0.5 flex items-center gap-0.5 tabular-nums">
                          {verse.verse}
                          <Show when={bookmarkSet().has(verse.verse)}>
                            <Bookmark
                              class="w-3 h-3 fill-warning text-warning"
                              data-testid={`bookmark-indicator-${verse.verse}`}
                            />
                          </Show>
                        </span>
                        <p
                          class="leading-[1.85] tracking-[0.003em] flex-1 text-foreground/95 font-serif select-text"
                          classList={{
                            "text-[0.95rem] md:text-base": readerStore.fontSize === "small",
                            "text-[1.085rem] md:text-[1.125rem]": readerStore.fontSize === "medium",
                            "text-[1.25rem] md:text-[1.35rem]": readerStore.fontSize === "large",
                          }}
                        >
                          <For each={formatVerseText(verse.text, readerStore.showStrongs)}>
                            {(token) => {
                              if (typeof token === "string") {
                                return <span>{token}</span>;
                              }
                              return (
                                <span
                                  class="align-super text-[10px] font-semibold text-warning cursor-pointer mx-0.5 hover:underline select-none dict-link"
                                  data-strong={token.code}
                                >
                                  {token.label}
                                </span>
                              );
                            }}
                          </For>
                        </p>
                      </div>
                    )}
                  </For>
                </Show>
              </Show>
            </div>
          </div>

          {/* Selected Verses Action Bar */}
          <VerseActionsBar
            userId={userId()}
            version={version()}
            bookId={bookId()}
            bookAbbr={bookAbbr()}
            chapter={Number.parseInt(chapter(), 10)}
            bookName={bookName()}
            verses={verses() ?? []}
            drizzle={drizzle()}
            bookmarkedVerses={bookmarkSet()}
            compose={composeMode()}
            onComposeChange={(next) => {
              patchReaderSearch({ compose: next ?? undefined });
            }}
            onClearSelection={() => {
              patchReaderSearch({ sel: undefined, compose: undefined });
            }}
          />
        </div>

        {/* Responsive Strong Concordance Panel (reader feature) */}
        <Show when={selectedStrongCode()}>
          {(code) => (
            <AppErrorBoundary variant="region" scope="strong-panel">
              <StrongConcordancePanel
                code={code()}
                db={drizzle()}
                onClose={() => setSelectedStrongCode(null)}
                onSelectCode={setSelectedStrongCode}
              />
            </AppErrorBoundary>
          )}
        </Show>
      </div>
    </Show>
  );
}
